import React from 'react';
import { images, profile, education, stats } from '../content_helper';
const About = () => {
  return (
    <main className="pt-32 pb-stack-lg max-w-container-max mx-auto px-margin-page">
        {/*  Hero Section  */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center mb-stack-lg">
            <div className="lg:col-span-7">
                <div
                    className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary border border-primary/20 rounded-full mb-stack-sm">
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                    <span className="font-label text-label-md">Senior Solutions Architect</span>
                </div>
                <h1 className="font-headline text-headline-xl mb-stack-md text-on-surface leading-tight">
                    Architecting Digital <span className="text-primary">Ecosystems</span> for Modern Governance.
                </h1>
                <p className="font-body text-body-lg text-on-surface-variant mb-gutter max-w-2xl">
                    With over 6 years of expertise in full-stack web and mobile development, I bridge the gap between
                    complex institutional requirements and high-performance technical solutions.
                </p>
            </div>
            <div className="lg:col-span-5 relative group">
                <div
                    className="absolute -inset-4 bg-primary/10 blur-3xl opacity-20 group-hover:opacity-40 transition-opacity">
                </div>
                <div
                    className="relative overflow-hidden rounded-xl border border-outline-variant/30 glass-panel aspect-square">
                    <img alt={`Professional portrait of ${profile.name}`}
                        className="w-full h-full object-cover grayscale opacity-90 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                        src={images.profile} />
                    <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-surface to-transparent">
                        <p className="font-mono text-code-sm text-primary opacity-70">SYST_INIT: ARCHITECT_LOADED</p>
                    </div>
                </div>
            </div>
        </section>
        {/*  Bento Grid Info  */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-gutter mb-stack-lg">
            {/*  Mission Card  */}
            <div className="md:col-span-2 glass-panel p-8 rounded-xl flex flex-col justify-between glowing-edge">
                <div>
                    <div className="mb-gutter">
                        <span className="material-symbols-outlined text-primary text-4xl"
                            data-icon="rocket_launch">rocket_launch</span>
                    </div>
                    <h2 className="font-headline text-headline-lg mb-stack-md text-on-surface">Mission Statement</h2>
                    <p className="font-body text-body-md text-on-surface-variant leading-relaxed">
                        My primary focus is engineering high-scale government platforms that serve millions, ensuring
                        absolute reliability and data integrity. Beyond the current landscape, I am deeply committed to
                        exploring the intersections of <span className="text-primary font-semibold">AI-driven
                            automation</span> and <span className="text-primary font-semibold">IoT integration</span> to
                        build the next generation of smart infrastructure.
                    </p>
                </div>
                <div className="mt-stack-lg flex gap-4">
                    <div className="px-4 py-2 bg-primary/10 rounded-lg border border-primary/20">
                        <span className="font-label text-label-md text-primary">Scalability First</span>
                    </div>
                    <div className="px-4 py-2 bg-primary/10 rounded-lg border border-primary/20">
                        <span className="font-label text-label-md text-primary">Public Impact</span>
                    </div>
                </div>
            </div>
            {/*  Experience Stats  */}
            <div className="glass-panel p-8 rounded-xl border-t-4 border-primary/50">
                <h3 className="font-label text-label-md uppercase tracking-widest text-on-surface-variant mb-stack-lg">
                    Technical Snapshot</h3>
                <div className="space-y-stack-md">
                    {stats.map((stat, index) => (
                        <div key={index} className="flex items-end justify-between border-b border-outline-variant/10 pb-2">
                            <span className="font-body text-body-sm text-on-surface-variant">{stat.label}</span>
                            <span className="font-headline text-headline-md text-primary">{stat.value}</span>
                        </div>
                    ))}
                </div>
                <div className="mt-stack-lg">
                    <img alt="Data visualization dashboard"
                        className="w-full h-32 object-cover rounded-lg opacity-40 mix-blend-screen"
                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuDhK1nN1PeAm2HV2Rp69B3OrHhzJJfSZK03oe4_2-okMzKRCwEnOSXZ7-hECKs0kXCtDaUYVXttUPBjzAFJyoKp_uu08Ruu4CsoywE_dzgP2R5IxP6uj09jCxE_PSkVwAuZjb8-TPTeOQuhMLzIxInghmuEufXaxDPEelbhMg9xvUERZnCl62i7MfJNAKE1uckaTqdNNtdQ7CoXCG6hWo9lnmEEpKBXFQvM_hyEXYrv_XJyyUJzj3_y5D1-K0yjFBoUhvOaxBg2px8" />
                </div>
            </div>
        </section>
        {/*  Education & Timeline  */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-stack-lg">
            {/*  Academic Foundation  */}
            <div className="space-y-gutter">
                <h2 className="font-headline text-headline-lg flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary" data-icon="school">school</span>
                    Education
                </h2>
                <div className="glass-panel p-8 rounded-xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                        <span className="material-symbols-outlined text-8xl"
                            data-icon="account_balance">account_balance</span>
                    </div>
                    <div className="relative z-10">
                    <h3 className="font-headline text-headline-md text-primary mb-1">{education[0].degree}</h3>
                            <a href={education[0].href} target="_blank" rel="noopener noreferrer" className="block text-primary hover:underline mb-stack-md">{education[0].institution}</a>
                            <div className="inline-block px-3 py-1 bg-primary/10 text-primary rounded border border-primary/20 mb-stack-md">
                            <span className="font-label text-label-md">{education[0].focus}</span>
                        </div>
                        <p className="font-body text-body-md text-on-surface-variant mb-stack-sm">{education[0].learnedIntro}</p>
                        <ul className="space-y-2">
                            {education[0].topics.map((topic, index) => (
                                <li key={index} className="font-body text-body-md text-on-surface-variant flex items-center gap-3">
                                    <span className="w-2 h-2 bg-primary/60 rounded-full shrink-0"></span>
                                    {topic}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            {/*  Terminal/Code Style Bio  */}
            <div className="space-y-gutter">
                <h2 className="font-headline text-headline-lg flex items-center gap-3">
                    <span className="material-symbols-outlined text-primary" data-icon="terminal">terminal</span>
                    Core Bio.json
                </h2>
                <div
                    className="bg-surface-container-lowest border border-outline-variant/20 rounded-xl overflow-hidden glass-panel">
                    <div
                        className="bg-surface-container-highest px-4 py-2 flex items-center gap-2 border-b border-outline-variant/10">
                        <div className="w-3 h-3 rounded-full bg-error/40"></div>
                        <div className="w-3 h-3 rounded-full bg-primary/40"></div>
                        <div className="w-3 h-3 rounded-full bg-secondary/40"></div>
                        <span className="font-mono text-code-sm ml-4 opacity-50">profile_v6.sh</span>
                    </div>
                    <div className="p-6 font-mono text-code-sm text-on-surface-variant overflow-x-auto">
                        <p className="text-primary"><span className="text-tertiary">const</span> dinesh = {"{"}</p>
                        <p className="pl-4">role: <span className="text-primary">"Senior Solutions Architect"</span>,</p>
                        <p className="pl-4">expertise: [<span className="text-primary">"Web Systems"</span>, <span
                                className="text-primary">"Native Mobile"</span>, <span
                                className="text-primary">"DevOps"</span>],</p>
                        <p className="pl-4">current_focus: <span className="text-primary">"Large-scale Government
                                GovTech"</span>,</p>
                        <p className="pl-4">emerging_tech: {"{"}</p>
                        <p className="pl-8">ai: <span className="text-primary">"Autonomous Workflows"</span>,</p>
                        <p className="pl-8">iot: <span className="text-primary">"Smart City Integration"</span></p>
                        <p className="pl-4">{"}"},</p>
                        <p className="pl-4">motto: <span className="text-primary">"Scale with Precision, Design with
                                Empathy"</span></p>
                        <p className="text-primary">{"}"};</p>
                        <div className="mt-4 border-t border-outline-variant/10 pt-4 text-primary">
                            <span className="animate-pulse">_</span> system ready for deployment.
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </main>
  );
};

export default About;
